import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_TRANSACTION,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Toggle,
  filterByCellValueMenuItem,
  filterExistsAndExcludes,
  getShowJSONContextItem,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWSFilterPipe,
  useWsBlotterTable,
  type CustomerTransaction,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, RowDoubleClickedEvent, StateUpdatedEvent } from 'ag-grid-community';
import { pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useTradingSettings } from '../../../providers/AppConfigProvider';
import { defaultCustomerTransactionColumns, defaultCustomerTransactionFilter } from './defaults';
import type { CustomerTransactionsFilter, CustomerTransactionsTabsProps } from './types';
import { useCustomerTransactionColumns } from './useCustomerTransactionColumns';
import { colIDToFilterBuilderKey, useCustomerTransactionsFilter } from './useCustomerTransactionsFilter';

function getCustomerTransactionKey(ct: CustomerTransaction) {
  return ct.TransactionID;
}

interface CustomerTransactionsBlotterProps {
  blotterID: string;
  tab: CustomerTransactionsTabsProps;
  onUpdateTab: (tab: CustomerTransactionsTabsProps) => void;
  initialFiltersOpen?: boolean;
  onCloneTab: () => void;
  onRowDoubleClicked: (event: RowDoubleClickedEvent<CustomerTransaction>) => void;
}

export const CustomerTransactionsBlotter = ({
  blotterID,
  tab,
  initialFiltersOpen,
  onCloneTab,
  onRowDoubleClicked,
  onUpdateTab,
}: CustomerTransactionsBlotterProps) => {
  const mixpanel = useMixpanel();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const defaultBlotterColumns = tab.defaultColumns ?? defaultCustomerTransactionColumns;
  const defaultFilter = tab.defaultFilter ?? defaultCustomerTransactionFilter;
  const showAllDecimals = tab.showAllDecimals ?? false;
  const setShowAllDecimals = useCallbackRef((showAllDecimals: boolean) => {
    onUpdateTab({ ...tab, showAllDecimals });
  });

  const defaultColumns = useCustomerTransactionColumns(defaultBlotterColumns);
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useCustomerTransactionsFilter({
    persistedBlotterTable,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    mixpanel.track(MixpanelEvent.ChangeBlotterColumns, {
      [MixpanelEventProperty.TabLabel]: 'Customer Transfers',
    });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialFiltersOpen },
    filterBuilderProps: filterResults.filterBuilderProps,
  });

  const rxjsFilterFunc = useCallback(
    (data: CustomerTransaction) => {
      if (filterExistsAndExcludes(filterResults.filter, 'Currencies', data, 'Currency')) {
        return false;
      }

      if (filterExistsAndExcludes(filterResults.filter, 'Counterparties', data, 'Counterparty')) {
        return false;
      }

      if (filterExistsAndExcludes(filterResults.filter, 'Statuses', data, 'Status')) {
        return false;
      }

      if (filterExistsAndExcludes(filterResults.filter, 'TransactionTypes', data, 'TransactionType')) {
        return false;
      }

      return true;
    },
    [filterResults.filter]
  );
  const filterPipe = useWSFilterPipe({ getUniqueKey: getCustomerTransactionKey, filterFunc: rxjsFilterFunc });

  const { handleClickJson, jsonModal } = useJsonModal();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => [
      ...filterByCellValueMenuItem({
        params,
        openClause: filterBuilderAccordion.openClause,
        filterableProperties: filterBuilderProps.properties,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      'separator',
      getShowJSONContextItem({ params, handleClickJson }),
      'separator',
      ...getDefaultContextMenuItems(params),
    ],
    [
      filterBuilderAccordion.openClause,
      filterBuilderProps.properties,
      mixpanel,
      handleClickJson,
      getDefaultContextMenuItems,
    ]
  );

  const request = useMemo(
    () =>
      ({
        name: CUSTOMER_TRANSACTION,
        tag: blotterID,
        sort_by: '-Timestamp',
        ...removeEmptyFilters(onlyServerKeys(filterResults.filter)),
      }) satisfies WebsocketRequest,
    [blotterID, filterResults.filter]
  );

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, CustomerTransaction>({
    request,
    pipe: filterPipe,
    rowID: 'TransactionID' satisfies keyof CustomerTransaction,
    columns: defaultColumns,
    handleClickJson,
    persistence: persistedBlotterTable,
    settings: blotterTableSettings,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
      getContextMenuItems,
    },
    exportOptions: {
      name: 'Customer Transfers',
      tabLabel: tab.label,
    },
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        {...filterResults.blotterTableFilterProps}
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {!showAllDecimalsInBlotters && (
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals}
                  onChange={setShowAllDecimals}
                  label="Show All Decimals"
                  data-testid="show-all-decimals-toggle"
                />
              )}
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {jsonModal}
    </>
  );
};

function onlyServerKeys(filter: CustomerTransactionsFilter | undefined): CustomerTransactionsFilter {
  if (filter == null) {
    return {};
  }

  return pick(filter, ['StartDate', 'EndDate', 'TransactionTypes']);
}
